import type { FC, ReactNode } from "react"
import { memo } from "react"
import { format } from "date-fns"

import type { Feedback } from "@productlane/db"
import { feedbackOriginPhrase, feedbackOriginText } from "@productlane/lib"
import { cn } from "@productlane/lib/src/cn"
import { Tooltip } from "@productlane/ui"

import { FeedbackOriginIconRecord } from "@/lib/feedback"

interface Props {
	origin: Feedback["origin"]
	/** Names the source of a custom channel thread, such as "WhatsApp". */
	customChannel?: string | null
	showLabel?: boolean
	className?: string
	createdAt?: number | Date | string
	updatedAt?: number | Date | string
	/** Replaces the tooltip's first line. */
	phrase?: string
	/** Replaces the origin icon; must render a DOM element so the tooltip can anchor to it. */
	icon?: ReactNode
}

export const OriginBadge: FC<Props> = memo(
	({
		origin,
		customChannel,
		className,
		showLabel = false,
		createdAt,
		updatedAt,
		phrase,
		icon,
	}) => {
		const Icon = FeedbackOriginIconRecord[origin]
		const text = feedbackOriginText(origin, customChannel)
		const tooltipText = phrase ?? feedbackOriginPhrase(origin, customChannel)

		const tooltipLabel =
			createdAt || updatedAt ? (
				<div className="flex flex-col gap-1">
					<span>{tooltipText}</span>
					<div className="flex flex-col text-tertiary">
						{createdAt && (
							<span>
								Created {format(new Date(createdAt), "MMM d, yyyy 'at' h:mm a")}
							</span>
						)}
						{updatedAt && (
							<span>
								Updated {format(new Date(updatedAt), "MMM d, yyyy 'at' h:mm a")}
							</span>
						)}
					</div>
				</div>
			) : (
				tooltipText
			)

		return (
			<span className="flex shrink-0 items-center gap-1.5">
				<Tooltip label={tooltipLabel}>
					{icon ?? (
						<Icon
							strokeWidth={1.75}
							className={cn(
								"h-4 w-4 text-gray-500 dark:text-gray-400 [&:not(.lucide)]:fill-gray-500 [&:not(.lucide)]:dark:fill-gray-400",
								className,
							)}
						/>
					)}
				</Tooltip>
				{showLabel && (
					<span className="truncate text-xs text-secondary">{text}</span>
				)}
			</span>
		)
	},
)

OriginBadge.displayName = "OriginBadge"
